import moment from "moment";
import type { TFunction } from "i18next";
import {
  ContractDurationType,
  FrameworkAgreementType,
  type ContractDurationDto,
  type SourcingEventDto,
} from "../../../../../autogen/bff-api";
import { isFoaPartIII, isUtilitiesPartII } from "../../../../../common/procurement/foaPartII";
import { announcedExtensionMonths } from "./extensionLength";

export type FrameworkDurationLimit = { kind: "FOA_PART_III" | "UTILITIES_PART_II"; years: number; legalBasis: string };

const FOA_PART_III_LIMIT: FrameworkDurationLimit = { kind: "FOA_PART_III", years: 4, legalBasis: "FOA § 26-1 (4)" };
const UTILITIES_PART_II_LIMIT: FrameworkDurationLimit = {
  kind: "UTILITIES_PART_II",
  years: 8,
  legalBasis: "forsyningsforskriften § 22-1 (4)",
};

// Mirrors SourcingEvent.frameworkDurationLimitExceeded in the BFF.
export const frameworkDurationLimitExceeded = (event: SourcingEventDto): FrameworkDurationLimit | null => {
  const limit = frameworkDurationLimit(event);
  return limit && runsOver(limit.years, event.eventData.contractDuration, announcedExtensionMonths(event)) ? limit : null;
};

export const frameworkLimitWords = (t: TFunction, limit: FrameworkDurationLimit) =>
  limit.kind === "UTILITIES_PART_II"
    ? {
        period: t("eight years"),
        basis: limit.legalBasis,
        rule: t(
          "A framework agreement may not run for more than eight years unless special circumstances justify it, for example the agreement's purpose, investment costs or the users' needs (forsyningsforskriften § 22-1 (4)).",
        ),
      }
    : {
        period: t("four years"),
        basis: limit.legalBasis,
        rule: t(
          "A framework agreement may not run for more than four years unless special circumstances justify it, for example the agreement's purpose, investment costs or the users' needs (FOA § 26-1 (4)).",
        ),
      };

const frameworkDurationLimit = (event: SourcingEventDto): FrameworkDurationLimit | null => {
  const context = event.eventContext.type === "Public" ? event.eventContext : null;
  if (!context) return null;
  const agreement = context.frameworkAgreement;
  if (!agreement || agreement === FrameworkAgreementType.None) return null;
  if (isFoaPartIII(context.regulation, context.foaPart)) return FOA_PART_III_LIMIT;
  if (isUtilitiesPartII(context.regulation, context.foaPart)) return UTILITIES_PART_II_LIMIT;
  return null;
};

export const runsOver = (
  years: number,
  duration: ContractDurationDto | null | undefined,
  extensionMonths = 0,
): boolean => {
  if (!duration) return false;
  switch (duration.type) {
    case ContractDurationType.FixedDates:
      return (
        !!duration.startDate &&
        !!duration.endDate &&
        moment(duration.endDate)
          .add(1, "day")
          .add(extensionMonths, "months")
          .isAfter(moment(duration.startDate).add(years, "years"), "day")
      );
    case ContractDurationType.PeriodBased:
      return (duration.periodMonths ?? 0) + extensionMonths > years * 12;
    case ContractDurationType.OpenEnded:
      return true;
    default:
      return false;
  }
};
